/**
 * @fileoverview Subscription page component.
 * @summary Displays available subscription plans and pricing.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/SubscriptionPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { fetchEvents } from '../../store/slices/eventsSlice'
import { fetchVenues } from '../../store/slices/venuesSlice'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import PageHeader from '../../components/PageHeader'
import PlanCard from './components/PlanCard'
import OneShotConfiguration from './components/OneShotConfiguration'
import ComparisonTable from './components/ComparisonTable'
import { ConfirmModal } from '../../components/ConfirmModal'
import { EventFormFields } from './components/EventFormFields'
import PlanChangeConfirmModal from './components/PlanChangeConfirmModal'
import Spinner from '../../components/Spinner'
import type { FC } from 'react'
import { useState, useEffect } from 'react'
import { createCheckoutSession, createCheckoutSessionForEvent, cancelSubscription } from '../../services/api.js'
import { localToUtcISOString } from '../../utils/date.js'

/**
 * @summary Subscription page component with support for event conversion.
 * @returns {ReactElement} Subscription page.
 */
const SubscriptionPage: FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const user = useAppSelector(state => state.auth.user)
  const events = useAppSelector(state => state.events.events)
  const venues = useAppSelector(state => state.venues.venues)
  const currentPlanType = (user?.subscription?.type as SubscriptionType) || SubscriptionType.FREE
  
  // Fetch events and venues on mount
  useEffect(() => {
    if (user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchEvents(false)) // Don't include hidden events
      dispatch(fetchVenues())
    }
  }, [dispatch, user])
  
  // State to track if ONE_SHOT plan is being configured
  const [isConfiguringOneShot, setIsConfiguringOneShot] = useState(false)
  
  // State to track which mode for ONE_SHOT: 'convert' or 'create'
  const [oneShotMode, setOneShotMode] = useState<'convert' | 'create'>('convert')

  // Loading state for checkout
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [errorPlanType, setErrorPlanType] = useState<SubscriptionType | null>(null)
  
  // State for cancel subscription modal
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState<string | null>(null)
  const [successPlanType, setSuccessPlanType] = useState<SubscriptionType | null>(null)
  
  // State for plan change confirmation modal
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false)
  const [pendingPlanChange, setPendingPlanChange] = useState<SubscriptionType | null>(null)
  
  // State for ONE_SHOT event creation form modal
  const [showCreateEventModal, setShowCreateEventModal] = useState(false)
  const [eventFormData, setEventFormData] = useState({
    name: '',
    description: '',
    venueId: '',
    date: '',
    time: '',
  })
  
  // Get eventId from query params (Cas 1: convert specific event)
  const eventIdFromUrl = searchParams.get('eventId')
  const eventToConvert = eventIdFromUrl ? (events.find(e => e.id === eventIdFromUrl) ?? null) : null
  
  // Get FREE events for Cas 2 (select which event to convert)
  // Allow conversion of draft and active events (active events might have hit song limits)
  const freeEvents = events.filter(e => (e.planType === SubscriptionType.FREE || !e.planType) && (e.status === 'draft' || e.status === 'active'))
  
  // State for Cas 2: which event user wants to convert
  const [selectedEventId, setSelectedEventId] = useState<string | null>(eventToConvert?.id || null)

  /**
   * @summary Handle subscription cancellation after confirmation.
   */
  const handleConfirmCancel = async () => {
    setIsCancelling(true)
    setCheckoutError(null)
    setErrorPlanType(null)
    setCancelSuccessMessage(null)
    setSuccessPlanType(null)

    try {
      const response = await cancelSubscription()

      if (!response.success) {
        setCheckoutError(response.message || t('subscription.cancelError'))
        setErrorPlanType(SubscriptionType.FREE)
        setIsCancelling(false)
        setShowCancelModal(false)
        return
      }

      // Show success message
      const endDate = response.data?.subscription?.currentPeriodEnd
        ? new Date(response.data.subscription.currentPeriodEnd).toLocaleDateString()
        : t('subscription.endOfBillingPeriod')
      
      setCancelSuccessMessage(t('subscription.accessUntil', { date: endDate }))
      setSuccessPlanType(SubscriptionType.FREE)
      setIsCancelling(false)
      setShowCancelModal(false)

      // Refresh user data after a short delay
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error: any) {
      setCheckoutError(error.message || t('subscription.cancelError'))
      setErrorPlanType(SubscriptionType.FREE)
      setIsCancelling(false)
      setShowCancelModal(false)
    }
  }

  /**
   * @summary Handle plan selection and initiate checkout or cancellation.
   */
  const handlePlanSelect = async (planType: SubscriptionType) => {
    // If clicking on current paid plan, show cancellation modal
    if (planType === currentPlanType && (planType === SubscriptionType.PRO || planType === SubscriptionType.PREMIUM || planType === SubscriptionType.ULTIMATE)) {
      setShowCancelModal(true)
      return
    }

    if (planType === SubscriptionType.FREE) {
      // FREE plan - confirm cancellation
      if (currentPlanType === SubscriptionType.FREE) {
        // Already on FREE plan, nothing to do
        return
      }

      // Always show modal (content will differ if cancellation is already in progress)
      setShowCancelModal(true)
      return
    }

    if (planType === SubscriptionType.ONE_SHOT) {
      // Show ONE_SHOT configuration
      setIsConfiguringOneShot(!isConfiguringOneShot)
      return
    }

    // Check if user has an existing paid subscription (PRO, PREMIUM, ULTIMATE)
    // If yes, show confirmation modal before changing plan
    if (currentPlanType !== SubscriptionType.FREE && currentPlanType !== SubscriptionType.ONE_SHOT) {
      setPendingPlanChange(planType)
      setShowPlanChangeModal(true)
      return
    }

    // Start checkout for premium plans (new subscription or FREE user upgrading)
    await handleCheckout(planType)
  }

  /**
   * @summary Handle plan change confirmation.
   */
  const handleConfirmPlanChange = async () => {
    if (!pendingPlanChange) return
    
    setShowPlanChangeModal(false)
    await handleCheckout(pendingPlanChange)
    setPendingPlanChange(null)
  }

  /**
   * @summary Initiate Stripe checkout session.
   */
  const handleCheckout = async (planType: SubscriptionType) => {
    setIsCheckingOut(true)
    setCheckoutError(null)
    setErrorPlanType(null)
    setCancelSuccessMessage(null)
    setSuccessPlanType(null)

    try {
      const response = await createCheckoutSession(planType)

      if (!response.success) {
        setCheckoutError(response.message || t('subscription.checkoutError'))
        setErrorPlanType(planType)
        setIsCheckingOut(false)
        return
      }

      // Check if subscription was updated directly (no checkout session needed)
      if (response.data?.updated === true) {
        // Subscription updated successfully, refresh user data and show success message
        setSuccessPlanType(planType)
        setCancelSuccessMessage(t('subscription.updateSuccess', { plan: t(`subscription.plans.${planType}.title`) }))
        setIsCheckingOut(false)
        
        // Refresh user data to get updated subscription
        // The webhook will update the database, but we need to refresh the frontend state
        window.location.reload() // Simple reload for now, could be improved with Redux refresh
        return
      }

      // Redirect to Stripe Checkout (new subscription)
      const { url } = response.data || {}
      if (url) {
        window.location.href = url
      } else {
        setCheckoutError(t('subscription.checkoutError'))
        setErrorPlanType(planType)
        setIsCheckingOut(false)
      }
    } catch (error: any) {
      setCheckoutError(error.message || t('subscription.checkoutError'))
      setErrorPlanType(planType)
      setIsCheckingOut(false)
    }
  }

  /**
   * @summary Handle converting an event to ONE_SHOT plan.
   */
  const handleConvertEventToOneShot = async (eventId: string) => {
    setIsCheckingOut(true)
    setCheckoutError(null)
    setErrorPlanType(null)
    setCancelSuccessMessage(null)
    setSuccessPlanType(null)

    try {
      const response = await createCheckoutSessionForEvent(eventId)

      if (!response.success) {
        setCheckoutError(response.message || t('subscription.upgradeError'))
        setErrorPlanType(SubscriptionType.ONE_SHOT)
        setIsCheckingOut(false)
        return
      }

      // Redirect to Stripe Checkout
      const { url } = response.data || {}
      if (url) {
        window.location.href = url
      } else {
        setCheckoutError(t('subscription.upgradeError'))
        setErrorPlanType(SubscriptionType.ONE_SHOT)
        setIsCheckingOut(false)
      }
    } catch (error: any) {
      setCheckoutError(error.message || t('subscription.upgradeError'))
      setErrorPlanType(SubscriptionType.ONE_SHOT)
      setIsCheckingOut(false)
    }
  }

  /**
   * @summary Open modal to create a new ONE_SHOT event.
   */
  const handleOpenCreateEventModal = () => {
    // Initialize form with empty values
    setEventFormData({
      name: '',
      description: '',
      venueId: '',
      date: '',
      time: '',
    })
    setShowCreateEventModal(true)
  }

  /**
   * @summary Handle creating a new ONE_SHOT event from form data.
   * Creates checkout session with event data. Event will be created in webhook after payment.
   */
  const handleCreateOneShotEvent = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    if (!eventFormData.name.trim()) {
      setCheckoutError(`${t('events.name')} ${t('common.required')}`)
      setErrorPlanType(SubscriptionType.ONE_SHOT)
      setShowCreateEventModal(true) // Keep modal open to show error
      setIsCheckingOut(false)
      return
    }

    if (!eventFormData.venueId) {
      setCheckoutError(`${t('events.venue')} ${t('common.required')}`)
      setErrorPlanType(SubscriptionType.ONE_SHOT)
      setShowCreateEventModal(true) // Keep modal open to show error
      setIsCheckingOut(false)
      return
    }

    setIsCheckingOut(true)
    setCheckoutError(null)
    setErrorPlanType(null)
    setCancelSuccessMessage(null)
    setSuccessPlanType(null)
    setShowCreateEventModal(false)

    try {
      // Convert local date/time to UTC ISO string for backend
      const isoDateString = localToUtcISOString(eventFormData.date, eventFormData.time)
      
      // Create checkout session with event data (event will be created after payment in webhook)
      const eventData = {
        name: eventFormData.name.trim(),
        description: eventFormData.description.trim() || undefined,
        date: isoDateString,
        venueId: eventFormData.venueId,
      }

      const response = await createCheckoutSessionForEvent(eventData)

      if (!response.success) {
        setCheckoutError(response.message || t('subscription.upgradeError'))
        setErrorPlanType(SubscriptionType.ONE_SHOT)
        setIsCheckingOut(false)
        return
      }

      // Redirect to Stripe Checkout
      const { url } = response.data || {}
      if (url) {
        window.location.href = url
      } else {
        setCheckoutError(t('subscription.upgradeError'))
        setErrorPlanType(SubscriptionType.ONE_SHOT)
        setIsCheckingOut(false)
      }
    } catch (error: any) {
      setCheckoutError(error.message || t('subscription.createEventError') || 'Erreur lors de la création de l\'événement')
      setErrorPlanType(SubscriptionType.ONE_SHOT)
      setIsCheckingOut(false)
    }
  }

  const plans = [
    {
      type: SubscriptionType.FREE,
      titleKey: 'subscription.planFree',
      descriptionKey: 'subscription.planFreeDescription',
      priceKey: 'subscription.priceFreeFree',
      noteKey: 'subscription.freePlanNote',
      
    },
    {
      type: SubscriptionType.ONE_SHOT,
      titleKey: 'subscription.planOneShot',
      descriptionKey: 'subscription.planOneShotDescription',
      priceKey: 'subscription.priceOneShotPer',
      noteKey: 'subscription.oneShotPlanNote',
    },
    {
      type: SubscriptionType.PRO,
      titleKey: 'subscription.planPro',
      descriptionKey: 'subscription.planProDescription',
      priceKey: 'subscription.priceProPer',
      noteKey: 'subscription.proPlanNote',
      noteEngagementKey: 'subscription.noEngagement',
    },
    {
      type: SubscriptionType.PREMIUM,
      titleKey: 'subscription.planPremium',
      descriptionKey: 'subscription.planPremiumDescription',
      priceKey: 'subscription.pricePremiumPer',
      noteKey: 'subscription.premiumPlanNote',
      noteEngagementKey: 'subscription.noEngagement',
    },
    {
      type: SubscriptionType.ULTIMATE,
      titleKey: 'subscription.planUltimate',
      descriptionKey: 'subscription.planUltimateDescription',
      priceKey: 'subscription.priceUltimatePer',
      noteKey: 'subscription.ultimatePlanNote',
      noteEngagementKey: 'subscription.noEngagement',
    },
  ]

  const isCurrentPlan = (planType: SubscriptionType) => planType === currentPlanType

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 rounded-lg pt-4">
      <PageHeader title={t('subscription.title')} subtitle={t('subscription.subtitle')} />

      <div className="container mx-auto px-4 py-12">
        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <PlanCard
              key={plan.type}
              plan={plan}
              isCurrentPlan={isCurrentPlan(plan.type)}
              subscriptionStatus={user?.subscription?.status as 'active' | 'canceled' | 'expired' | undefined}
              t={t}
              isLoading={isCheckingOut}
              onConfigure={() => {
                if (plan.type === SubscriptionType.ONE_SHOT) {
                  setIsConfiguringOneShot(!isConfiguringOneShot)
                } else {
                  handlePlanSelect(plan.type)
                }
              }}
            >
              {/* Success message for this plan */}
              {cancelSuccessMessage && successPlanType === plan.type && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <p className="text-green-600 dark:text-green-400 font-semibold text-sm">{t('subscription.cancelSuccess')}</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">{cancelSuccessMessage}</p>
                </div>
              )}

              {/* Error message for this plan */}
              {checkoutError && errorPlanType === plan.type && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-600 dark:text-red-400 font-semibold text-sm">{checkoutError}</p>
                </div>
              )}

              {plan.type === SubscriptionType.ONE_SHOT && isConfiguringOneShot && (
                <OneShotConfiguration
                  eventToConvert={eventToConvert}
                  freeEvents={freeEvents}
                  selectedEventId={selectedEventId}
                  onSelectEventId={setSelectedEventId}
                  oneShotMode={oneShotMode}
                  setOneShotMode={setOneShotMode}
                  onConvertEvent={handleConvertEventToOneShot}
                  onCreateEvent={handleOpenCreateEventModal}
                  isLoading={isCheckingOut}
                  t={t}
                />
              )}
            </PlanCard>
          ))}
        </div>

        {/* Comparison Table */}
        <ComparisonTable plans={plans} t={t} />

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">{t('subscription.contactSupport')}</p>
          <button
            onClick={() => window.open('https://karaonow.com/', '_blank')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors duration-200"
          >
            {t('subscription.learnMore')}
          </button>
        </div>
      </div>

      {/* Plan Change Confirmation Modal */}
      {currentPlanType !== SubscriptionType.FREE && currentPlanType !== SubscriptionType.ONE_SHOT && pendingPlanChange && (
        <PlanChangeConfirmModal
          isOpen={showPlanChangeModal}
          currentPlan={currentPlanType}
          newPlan={pendingPlanChange}
          onConfirm={handleConfirmPlanChange}
          onCancel={() => {
            setShowPlanChangeModal(false)
            setPendingPlanChange(null)
          }}
          isLoading={isCheckingOut}
        />
      )}

      {/* Cancel Subscription Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        title={user?.subscription?.status === 'canceled' ? t('subscription.cancellationInProgressTitle') : t('subscription.cancelModalTitle')}
        message={user?.subscription?.status === 'canceled' ? t('subscription.cancellationInProgressMessage') : t('subscription.cancelModalMessage')}
        confirmLabel={t('subscription.cancelModalConfirm')}
        cancelLabel={t('common.cancel')}
        variant="warning"
        onConfirm={user?.subscription?.status === 'canceled' ? undefined : handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        isLoading={isCancelling}
        infoOnly={user?.subscription?.status === 'canceled'}
      />

      {/* Create ONE_SHOT Event Modal */}
      {showCreateEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('subscription.createOneShotEventTitle') || 'Créer un événement unique'}
                </h2>
                <button
                  onClick={() => setShowCreateEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  disabled={isCheckingOut}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {checkoutError && errorPlanType === SubscriptionType.ONE_SHOT && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-600 dark:text-red-400 font-semibold text-sm">{checkoutError}</p>
                </div>
              )}

              <form onSubmit={handleCreateOneShotEvent} className="space-y-6">
                <EventFormFields
                  formData={eventFormData}
                  venues={venues}
                  onFormDataChange={(data) => setEventFormData({ ...eventFormData, ...data })}
                  disabled={isCheckingOut}
                />

                <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowCreateEventModal(false)}
                    className="btn btn-secondary flex-1"
                    disabled={isCheckingOut}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-1"
                    disabled={isCheckingOut || venues.length === 0}
                  >
                    {isCheckingOut ? (
                      <>
                        <Spinner size="sm" variant="white" />
                        <span className="ml-2">{t('common.processing')}</span>
                      </>
                    ) : (
                      <>
                        {t('subscription.proceedToPayment') || 'Procéder au paiement'} - {SUBSCRIPTION_PLANS[SubscriptionType.ONE_SHOT].priceEur}€
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionPage
